/** @jsx isolateComponent */

import React, { useState } from 'react';
import { Interpolation } from '@emotion/core';

import {
  Animation,
  Colors,
  Shadows,
  Typography,
} from '@zapier/design-system/src/theme';
import Button from '@zapier/design-system/src/components/navigation/Button'; // eslint-disable-line import/default
import Icon from '@zapier/design-system/src/components/display/Icon';
import IconButton from '@zapier/design-system/src/components/navigation/IconButton';
import {
  createKeyHandler,
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

type AccordionSize = 'small' | 'large';

type AccordionProps = {
  /**
   * Title, shown both when closed and open.
   */
  title: React.ReactNode;

  /**
   * Content, only shown when open.
   */
  children: React.ReactNode;

  /**
   * Optional link, visible as icon next to/under (depends on viewport) the content when open.
   */
  href?: string;

  /**
   * Lets a parent open the `Accordion` on its initial render.
   */
  isInitiallyOpen?: boolean;

  /**
   * When `small`, uses smaller padding and renders any link under instead of next to the content.
   *
   * @default large
   */
  size?: AccordionSize;
};

type DerivedState = {
  isFocused: boolean;
  isHovered: boolean;
  isOpen: boolean;
};

const Styles = labelStyles(__filename, {
  root: (derivedState: DerivedState) => [
    {
      position: 'relative',
      backgroundColor: Colors.grey000,
      border: `1px solid ${Colors.grey000}`,
      borderRadius: 5,
      transitionProperty: 'box-shadow, border-color, background-color',
      transitionTimingFunction: Animation.transitionTimingFunction,
      transitionDuration: Animation.transitionDuration,
    },
    (derivedState.isFocused || derivedState.isHovered || derivedState.isOpen) &&
      withModifier('highlighted', {
        backgroundColor: Colors.white,
        borderColor: Colors.grey200,
        borderRadius: 10,
        boxShadow: Shadows.elevation5,
      }),
  ],
  summary: ({ size }: { size: AccordionSize }, derivedState: DerivedState) => [
    {
      ...Typography.subtitle700,
      display: 'block',
      color: Colors.grey700,
      fontWeight: 600,
      cursor: 'pointer',
      listStyle: 'none',
      '&::-webkit-details-marker': {
        display: 'none',
      },
      // Remove marker from browser / polyfills
      '&::before': {
        content: '""',
      },
    },
    withModifier(
      size,
      {
        small: {
          padding: '20px 20px 20px 55px',
        },
        large: {
          padding: '25px 70px',
        },
      }[size]
    ),
    derivedState.isOpen &&
      withModifier(`open-${size}`, {
        // swapping padding for bottom so that when closed, you can tap anywhere
        // to open, after which the margin absorbs any (smaller) content margins
        paddingBottom: 0,
        marginBottom: size === 'small' ? 20 : 25,
        color: Colors.black,
      }),
    (derivedState.isFocused || derivedState.isHovered) &&
      withModifier('highlighted', {
        color: Colors.blue500,
      }),
  ],
  content: ({ size }: { size: AccordionSize }) => [
    {
      color: Colors.grey700,
    },
    withModifier(
      size,
      {
        small: {
          margin: 20,
        },
        large: {
          margin: `25px 70px`,
        },
      }[size]
    ),
  ],
  arrow: ({ size }: { size: AccordionSize }) => [
    {
      position: 'absolute',
    },
    withModifier(
      size,
      {
        small: {
          top: 20,
          left: 15,
        },
        large: {
          top: 25,
          left: 25,
        },
      }[size]
    ),
  ],
  fullPageButton: ({ isIconOnly }: { isIconOnly: boolean }) => [
    isIconOnly &&
      withModifier('icon-only', {
        position: 'absolute',
        top: 20,
        right: 20,
      }),
    !isIconOnly &&
      withModifier('full-button', {
        margin: 20,
      }),
  ],
});

const FullPageButton: React.FC<AccordionProps> = props => {
  const iconName = 'arrowOffsite';
  const isIconOnly = props.size === 'large';

  const button = isIconOnly ? (
    <IconButton
      color="white"
      href={props.href}
      icon={iconName}
      label="Open full page"
      tooltipPosition="west"
    />
  ) : (
    <Button
      casing="uppercase"
      color="greyAlt"
      href={props.href}
      iconBefore={iconName}
      isFullWidth={true}
      size="small"
    >
      Open full page
    </Button>
  );
  return (
    <div
      css={Styles.fullPageButton({
        isIconOnly,
      })}
    >
      {button}
    </div>
  );
};

const defaultProps = {
  size: 'large' as AccordionSize,
};

/**
 * A styled `<details>` component that opens and closes on click.
 *
 * Tags: core-ui
 */
const Accordion: React.FC<AccordionProps> = _props => {
  const props = {
    ...defaultProps,
    ..._props,
  };

  const { children, href, isInitiallyOpen, title, size } = props;

  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isOpen, setIsOpen] = useState(!!isInitiallyOpen);

  const derivedState = {
    isFocused,
    isHovered,
    isOpen,
  };

  const handleClick = (e: React.SyntheticEvent<EventTarget, Event>) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  return (
    <details css={Styles.root(derivedState) as Interpolation} open={isOpen}>
      <summary
        css={Styles.summary({ size }, derivedState)}
        onBlur={() => setIsFocused(false)}
        onClick={handleClick}
        onFocus={() => setIsFocused(true)}
        onKeyDown={createKeyHandler(['enter'], handleClick)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        role="button"
        tabIndex={0}
      >
        <span css={Styles.arrow({ size }) as Interpolation}>
          <Icon
            canAcceptPointerEvents={false}
            name={isOpen ? 'arrowSmallUp' : 'arrowSmallDown'}
            size={24}
          />
        </span>
        {title}
      </summary>
      {isOpen && <div css={Styles.content({ size })}>{children}</div>}
      {isOpen && href && <FullPageButton {...props} />}
    </details>
  );
};

Accordion.defaultProps = defaultProps;

export default Accordion;
